import { useState } from 'react';

import { isToday } from 'date-fns';
import { useAtomValue } from 'jotai';

import type { SelectedCountsFilter } from '../../../@types/appointments.js';
import {
  type TaskCounts,
  useGetTaskDisplayName,
} from '../../../react-query/useGetTasks';
import { usePreferences } from '../../../react-query/usePreferences';
import { mixpanelEvents, useTrackEvent } from '../../../utils/mixpanel';
import { selectDateAtom, selectedCountsFilterAtom } from '../atoms';
import { TaskProgressBar } from '../patientListMetaData/taskProgressBar';

import { FilterButton } from './FilterButton';
import { ViewAllButton } from './ViewAllButton';

const TaskCount = ({
  denominator,
  numerator,
}: {
  denominator?: number;
  numerator: number;
}) => (
  <p className="vai-typography-large !vai-font-medium vai-text-leaf-600">
    {numerator}
    {typeof denominator === 'number' && (
      <span className="vai-typography-large !vai-font-normal vai-text-slate-500">
        /{denominator}
      </span>
    )}
  </p>
);

interface Props {
  gamifiedTaskCounts: TaskCounts;
  loading: boolean;
  onChangeFilter: (filter?: SelectedCountsFilter) => void;
}

export const TasksFilters = ({
  gamifiedTaskCounts,
  loading,
  onChangeFilter,
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const selectedCountsFilter = useAtomValue(selectedCountsFilterAtom);
  const selectedDate = useAtomValue(selectDateAtom);

  const { data: prefs } = usePreferences();
  const { trackEvent } = useTrackEvent();
  const getTaskDisplayName = useGetTaskDisplayName();
  const handleChangeExpanded = (newIsExpanded: boolean) => {
    setIsExpanded(newIsExpanded);
    trackEvent(mixpanelEvents.userViewAllClick, {
      expanded: newIsExpanded ? 'all' : 'less',
      context: 'TASK_FILTERS',
      date: selectedDate,
      isToday: selectedDate ? isToday(selectedDate) : false,
    });
  };

  // these tasks are not shown in the sidebar by default, but instead are
  // hidden in an accordion section, if available
  const isEnabledProphy = prefs?.taskProphy !== 0;
  const isEnabledScanOpportunity = prefs.taskScanOpportunity !== 0;
  const isEnabledFluoride = prefs?.taskFluoride !== 0;

  // any tasks referenced above should be included here, as it controls
  // whether we show the closed accordion section
  const canExpand =
    isEnabledProphy || isEnabledScanOpportunity || isEnabledFluoride;

  return (
    <>
      {!loading && (
        <TaskProgressBar totalTaskCounts={gamifiedTaskCounts.total} />
      )}

      {prefs?.taskBitewings !== 0 && (
        <FilterButton
          count={
            <TaskCount
              denominator={gamifiedTaskCounts.taskCounts.BW_DUE.total}
              numerator={gamifiedTaskCounts.taskCounts.BW_DUE.complete}
            />
          }
          dataTestId="bw-due-filter"
          label={getTaskDisplayName('BW_DUE')}
          loading={loading}
          onClick={() => onChangeFilter('BW_DUE')}
          selected={selectedCountsFilter === 'BW_DUE'}
        />
      )}
      {prefs?.taskPerioChart !== 0 && (
        <FilterButton
          count={
            <TaskCount
              denominator={gamifiedTaskCounts.taskCounts.PERIO_CHART_DUE.total}
              numerator={gamifiedTaskCounts.taskCounts.PERIO_CHART_DUE.complete}
            />
          }
          dataTestId="perio-chart-due-filter"
          label={getTaskDisplayName('PERIO_CHART_DUE')}
          loading={loading}
          onClick={() => onChangeFilter('PERIO_CHART_DUE')}
          selected={selectedCountsFilter === 'PERIO_CHART_DUE'}
        />
      )}
      {prefs?.taskPerioMaintenance !== 0 && (
        <FilterButton
          count={
            <TaskCount
              denominator={
                gamifiedTaskCounts.taskCounts.PERIO_MAINTENANCE_DUE.total
              }
              numerator={
                gamifiedTaskCounts.taskCounts.PERIO_MAINTENANCE_DUE.complete
              }
            />
          }
          dataTestId="perio-maintenance-due-filter"
          label={getTaskDisplayName('PERIO_MAINTENANCE_DUE')}
          loading={loading}
          onClick={() => onChangeFilter('PERIO_MAINTENANCE_DUE')}
          selected={selectedCountsFilter === 'PERIO_MAINTENANCE_DUE'}
        />
      )}
      {isExpanded && (
        <>
          {isEnabledProphy && (
            <FilterButton
              count={
                <TaskCount
                  denominator={gamifiedTaskCounts.taskCounts.PROPHY_DUE.total}
                  numerator={gamifiedTaskCounts.taskCounts.PROPHY_DUE.complete}
                />
              }
              dataTestId="prophy-due-filter"
              label={getTaskDisplayName('PROPHY_DUE')}
              loading={loading}
              onClick={() => onChangeFilter('PROPHY_DUE')}
              selected={selectedCountsFilter === 'PROPHY_DUE'}
            />
          )}
          {isEnabledScanOpportunity && (
            <FilterButton
              count={
                <TaskCount
                  denominator={
                    gamifiedTaskCounts.taskCounts.SCAN_OPPORTUNITY.total
                  }
                  numerator={
                    gamifiedTaskCounts.taskCounts.SCAN_OPPORTUNITY.complete
                  }
                />
              }
              dataTestId="scan-opportunity-filter"
              label={getTaskDisplayName('SCAN_OPPORTUNITY')}
              loading={loading}
              onClick={() => onChangeFilter('SCAN_OPPORTUNITY')}
              selected={selectedCountsFilter === 'SCAN_OPPORTUNITY'}
            />
          )}
          {isEnabledFluoride && (
            <FilterButton
              count={
                <TaskCount
                  denominator={gamifiedTaskCounts.taskCounts.FLUORIDE_DUE.total}
                  numerator={
                    gamifiedTaskCounts.taskCounts.FLUORIDE_DUE.complete
                  }
                />
              }
              dataTestId="fluoride-due-filter"
              label={getTaskDisplayName('FLUORIDE_DUE')}
              loading={loading}
              onClick={() => onChangeFilter('FLUORIDE_DUE')}
              selected={selectedCountsFilter === 'FLUORIDE_DUE'}
            />
          )}
        </>
      )}
      {canExpand && !loading && (
        <ViewAllButton
          dataTestId="view-all-tasks-filter"
          expanded={isExpanded}
          onChange={handleChangeExpanded}
        />
      )}
    </>
  );
};
